/**
 * La hoja de tallas y el medidor.
 *
 * NO PIDE NADA A LA RED PARA ABRIR. Cada tarjeta trae su propio JSON (`aud-card-talla`) con las
 * tallas, los ids de variante y el «quedan 2». `/products/x.js` no devuelve inventario, así que
 * ir a buscarlo sería una petición por tarjeta y encima peor dato.
 *
 * UNA SOLA LISTA DE TALLAS (SOP-guia-tallas §7bis). Cada casilla lleva su talla, sus centímetros
 * y, solo si hace falta, «quedan 2», «agotada» o «aprox.». Con producto son botones que se
 * agregan; sin producto, la guía para leer.
 *
 * EL CARRITO SE AVISA COMO LO HACE HORIZON. `product-form.js` dispara `CartLinesUpdateEvent` de
 * `@shopify/events` ANTES del fetch y resuelve su promesa después; `cart-drawer.js` escucha eso
 * para abrirse solo y `cart-items-component` se repinta con las secciones que vienen en la
 * respuesta. Copiamos ese contrato exacto en vez de inventar uno: así el cajón del carrito se
 * comporta igual que si el botón fuera nativo, sin tocar un archivo de Horizon.
 *
 * EL CAJÓN SE ABRE SOLO CUANDO CERRAMOS. `cart-drawer.js` detecta que el evento nació dentro de
 * un `dialog:modal` y espera a su `close` para no robarse el foco. Por eso cerramos la hoja.
 */

const cfg = leerCfg();
const GUIAS = {
  hombre: filas(cfg.guias?.hombre, 'rango'),
  mujer: filas(cfg.guias?.mujer, 'punto'),
};

let genero = cfg.genero === 'mujer' ? 'mujer' : 'hombre';
let ficha = null;
let variante = null;

const hoja = document.getElementById('aud-hoja');
const pop = document.getElementById('aud-pop');

function leerCfg() {
  const n = document.getElementById('aud-tallas-cfg');
  if (!n) return {};
  try {
    return JSON.parse(n.textContent);
  } catch (e) {
    return {};
  }
}

/** «21,9» -> 219. Acepta coma y punto porque la guía la escribe una persona. */
function mm(v) {
  const n = parseFloat(String(v ?? '').replace(',', '.'));
  return Number.isFinite(n) ? Math.round(n * 10) : null;
}

function cm(v) {
  return (v / 10).toFixed(1).replace('.', ',');
}

function cmCorto(v) {
  return v % 10 === 0 ? String(v / 10) : cm(v);
}

function filas(texto, modo) {
  return String(texto || '')
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean)
    .map((l) => {
      const c = l.split('|').map((x) => x.trim());
      const der = /^deriv/i.test(c[modo === 'rango' ? 3 : 2] || '');
      return modo === 'rango'
        ? { t: c[0], ini: mm(c[1]), fin: mm(c[2]), der, modo }
        : { t: c[0], cm: mm(c[1]), der, modo };
    })
    .filter((f) => f.t && (f.modo === 'rango' ? f.ini != null : f.cm != null));
}

function tallaDe(valor) {
  const g = GUIAS[genero] || [];
  if (!g.length) return null;
  return g[0].modo === 'rango'
    ? g.find((f) => valor >= f.ini && valor <= f.fin) || null
    : g.find((f) => valor <= f.cm) || null;
}

function filaDe(t) {
  return (GUIAS[genero] || []).find((f) => f.t === t) || null;
}

/** «25–25,9» en hombre, «22,5» en mujer. Talla sin fila: nada (T-2, no se inventa). */
function medidaDe(f) {
  if (!f) return '';
  return f.modo === 'rango' ? `${cmCorto(f.ini)}–${cm(f.fin)}` : cmCorto(f.cm);
}

function el(tag, clase, texto) {
  const n = document.createElement(tag);
  if (clase) n.className = clase;
  if (texto != null) n.textContent = texto;
  return n;
}

/* ---------------------------------------------------------------- LA HOJA */

function abrirHoja(boton) {
  const caja = boton.closest('.aud-ct');
  const datos = caja?.querySelector('.aud-ct__datos');
  if (!datos) return;
  try {
    ficha = JSON.parse(datos.textContent);
  } catch (e) {
    return;
  }
  variante = null;
  if (ficha.genero === 'mujer' || ficha.genero === 'hombre') genero = ficha.genero;

  const enc = hoja.querySelector('[data-aud-enc]');
  enc.textContent = '';
  if (ficha.foto) {
    const img = el('img');
    img.src = ficha.foto;
    img.alt = '';
    enc.appendChild(img);
  }
  const col = el('div');
  col.appendChild(el('b', '', ficha.nombre || ''));
  if (ficha.ref) col.appendChild(el('div', 'aud-rotulito', ficha.ref));
  col.appendChild(el('strong', '', ficha.precio || ''));
  enc.appendChild(col);

  pintarRejilla(hoja.querySelector('[data-aud-tallas]'));

  fallo('');
  pintarBoton();
  if (!hoja.open) hoja.showModal();
}

/* Las casillas. Se pintan igual en la hoja y en el medidor: el mismo dato y la misma forma,
   para que nadie tenga que aprender dos cosas. `suya` es la talla que calculó el medidor.
   Sin producto en contexto las casillas son la guía: se leen, no se tocan (H-2). */
function pintarRejilla(rejilla, suya) {
  if (!rejilla) return;
  rejilla.textContent = '';
  const lista = ficha ? ficha.tallas || [] : (GUIAS[genero] || []).map((f) => ({ t: f.t, guia: true }));
  for (const t of lista) {
    const f = filaDe(t.t);
    const b = el(t.guia ? 'div' : 'button', 'aud-htalla' + (suya && t.t === suya ? ' is-suya' : ''));
    b.dataset.marca = cfg.tuTalla || 'Tu talla';
    if (!t.guia) {
      b.type = 'button';
      b.setAttribute('aria-pressed', String(!!variante && variante.t === t.t));
      if (!t.d) b.disabled = true;
      b.dataset.id = String(t.id);
      b.dataset.t = t.t;
    }
    b.appendChild(el('b', '', t.t));
    /* El número no se parte: en una casilla de 62 px «22–22,9 cm» baja a dos renglones, y
       mejor que baje «cm» entero que «22,9» suelto. */
    const medida = medidaDe(f);
    if (medida) {
      const sm = el('small');
      sm.append(el('span', 'aud-htalla__cm', medida), ' cm');
      b.appendChild(sm);
    }
    const aviso = t.guia
      ? f?.der ? cfg.aprox || 'aprox.' : ''
      : !t.d
        ? cfg.agotada || 'agotada'
        : t.q
          ? (cfg.pocas || 'quedan {n}').replace('{n}', String(t.q))
          : f?.der ? cfg.aprox || 'aprox.' : '';
    if (aviso) b.appendChild(el('em', '', aviso));
    rejilla.appendChild(b);
  }
}

/* En la ficha no hay tarjetas que tocar, así que el producto llega en un JSON suelto que pone
   `aud-talla-medida` dentro de un `.aud-ct` ESCONDIDO. Se busca ese y no «el primero que haya»:
   en la reja y en el inicio el primero es la primera tarjeta, y el medidor abierto desde el
   dock se habría quedado con un producto que nadie eligió. */
function fichaDeLaPagina() {
  if (ficha) return ficha;
  const n = document.querySelector('.aud-ct[hidden] .aud-ct__datos');
  if (!n) return null;
  try {
    ficha = JSON.parse(n.textContent);
    if (ficha.genero === 'mujer' || ficha.genero === 'hombre') genero = ficha.genero;
  } catch (e) {
    ficha = null;
  }
  return ficha;
}

function pintarBoton() {
  /* Hay dos: el de la hoja y el del medidor. Se pintan los dos porque los dos pueden estar
     abiertos en el mismo recorrido y ninguno debe quedarse con un texto viejo. */
  const plantilla = cfg.agregar || 'Agregar talla {t} al carrito';
  for (const b of document.querySelectorAll('[data-aud-agregar]')) {
    if (variante) {
      b.disabled = false;
      b.textContent = plantilla.includes('{t}') ? plantilla.replace('{t}', variante.t) : `${plantilla} · talla ${variante.t}`;
    } else {
      b.disabled = true;
      b.textContent = cfg.agregarVacio || 'Elige una talla';
    }
  }
}

function fallo(texto) {
  for (const p of document.querySelectorAll('[data-aud-error]')) {
    p.textContent = texto;
    p.hidden = !texto;
  }
}

async function agregar(boton) {
  if (!variante) return;
  const rutas = window.Theme?.routes || window.routes || {};
  const urlAdd = rutas.cart_add_url || '/cart/add';
  const urlCart = rutas.cart_url || '/cart';

  boton.disabled = true;
  fallo('');

  const cuerpo = new FormData();
  cuerpo.set('id', String(variante.id));
  cuerpo.set('quantity', '1');
  const secciones = [...document.querySelectorAll('cart-items-component')]
    .map((e) => e.dataset.sectionId)
    .filter(Boolean);
  if (secciones.length) cuerpo.append('sections', secciones.join(','));

  let SE = null;
  try {
    SE = await import('@shopify/events');
  } catch (e) {
    /* sin el módulo se agrega igual; solo no se avisa al cajón */
  }

  const diferida = SE?.CartLinesUpdateEvent?.createPromise?.();
  if (SE && diferida) {
    boton.dispatchEvent(
      new SE.CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: String(variante.id), quantity: 1 }],
        promise: diferida.promise,
      })
    );
  }

  try {
    const r = await fetch(urlAdd, {
      method: 'POST',
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: cuerpo,
    });
    const resp = await r.json();
    if (resp.status) throw new Error(resp.description || resp.message || 'No se pudo agregar');

    const carrito = await fetch(`${urlCart}.js`).then((x) => x.json());
    diferida?.resolve({
      cart: SE.CartLinesUpdateEvent.createCartFromAjaxResponse(carrito),
      detail: {
        items: carrito.items,
        source: 'aud-tallas',
        sourceId: 'aud-tallas',
        itemCount: 1,
        sections: resp.sections,
        didError: false,
      },
    });
    /* Se cierran los dos: `cart-drawer.js` espera el `close` del diálogo modal para no
       robarle el foco, y en este recorrido el abierto puede ser cualquiera de ellos. */
    if (pop.open) pop.close();
    if (hoja.open) hoja.close();
    // Sin el módulo de eventos el cajón no se entera: se va al carrito, que siempre funciona.
    if (!SE) window.location.href = urlCart;
  } catch (e) {
    diferida?.reject(e);
    fallo(e.message || 'No se pudo agregar');
    boton.disabled = false;
  }
}

/* ------------------------------------------------------------- EL MEDIDOR */

function abrirPop() {
  fichaDeLaPagina();
  const tabs = pop.querySelector('[data-aud-generos]');
  /* Con producto el género es el del producto: ofrecer «Mujer» en una bota de hombre solo
     sirve para equivocarse de talla. */
  tabs.hidden = !cfg.pildoras || !!ficha;
  for (const b of tabs.querySelectorAll('[data-aud-genero]')) {
    b.setAttribute('aria-pressed', String(b.dataset.audGenero === genero));
  }
  if (hoja.open) hoja.close();
  if (!pop.open) pop.showModal();
  pintarPop();
}

function pintarPop() {
  const g = GUIAS[genero] || [];
  if (!g.length) return;
  const esRango = g[0].modo === 'rango';
  const min = esRango ? g[0].ini - 5 : g[0].cm - 8;
  const max = esRango ? g[g.length - 1].fin : g[g.length - 1].cm + 12;

  const barra = pop.querySelector('[data-aud-rango]');
  barra.min = String(min);
  barra.max = String(max);
  if (+barra.value < min || +barra.value > max) barra.value = String(Math.round((min + max) / 2));

  const rot = pop.querySelector('[data-aud-lista-rot]');
  if (rot) rot.textContent = ficha ? cfg.rotuloTallas || 'Elige tu talla' : cfg.rotuloGuia || 'Todas las tallas';

  pintarProducto();
  pintarMedida();
}

/* El pie de compra: solo existe si hay un producto que comprar. Se rehace cada vez porque el
   producto cambia al abrir la hoja desde otra tarjeta. */
function pintarProducto() {
  const pie = pop.querySelector('[data-aud-pop-pie]');
  if (!pie) return;
  pie.hidden = !ficha;
  const prod = pop.querySelector('[data-aud-pop-prod]');
  if (!prod) return;
  prod.textContent = '';
  if (!ficha) return;
  if (ficha.foto) {
    const img = el('img');
    img.src = ficha.foto;
    img.alt = '';
    prod.appendChild(img);
  }
  const col = el('div');
  col.appendChild(el('b', '', ficha.nombre || ''));
  if (ficha.ref) col.appendChild(el('div', 'aud-rotulito', ficha.ref));
  prod.appendChild(col);
  prod.appendChild(el('strong', '', ficha.precio || ''));
}

function pintarMedida() {
  const v = +pop.querySelector('[data-aud-rango]').value;
  pop.querySelector('[data-aud-cm]').textContent = `${cm(v)} cm`;
  const f = tallaDe(v);
  pop.querySelector('[data-aud-talla]').textContent = f ? `${cfg.talla || 'Talla'} ${f.t}` : '—';

  const suya = f ? f.t : null;
  const enFicha = ficha && suya ? ficha.tallas?.find((t) => t.t === suya) : null;
  const enStock = enFicha && enFicha.d ? enFicha : null;

  /* La talla calculada se escoge sola, pero SOLO si hay: proponer una talla agotada y dejar el
     botón encendido es mandar al comprador contra una pared. Escogida a mano, manda la mano. */
  if (ficha && (!variante || variante.suya)) {
    variante = enStock ? { id: String(enStock.id), t: enStock.t, suya: true } : null;
  }

  /* La nota: una sola frase, la más importante que aplique. Las que piden ayuda llevan el
     enlace, porque ahí la respuesta ya no la da la guía. */
  let texto = cfg.notaNormal || '';
  let ayuda = false;
  if (!f) {
    texto = cfg.notaFuera || '';
    ayuda = true;
  } else if (ficha && !enFicha) {
    texto = cfg.notaNoViene || '';
    ayuda = true;
  } else if (f.der) {
    texto = cfg.notaDerivada || '';
    ayuda = true;
  } else if (ficha && !enStock) {
    texto = cfg.notaAgotada || '';
  }
  const nota = pop.querySelector('[data-aud-nota]');
  nota.textContent = texto;
  if (ayuda && cfg.ayudaUrl && cfg.ayudaTexto) {
    const a = el('a', '', cfg.ayudaTexto);
    a.href = cfg.ayudaUrl;
    a.target = '_blank';
    a.rel = 'noopener';
    nota.append(texto ? ' ' : '', a);
  }

  pintarRejilla(pop.querySelector('[data-aud-pop-tallas]'), suya);
  pintarBoton();
}

function mover(paso) {
  const barra = pop.querySelector('[data-aud-rango]');
  const v = Math.min(+barra.max, Math.max(+barra.min, +barra.value + paso));
  barra.value = String(v);
  pintarMedida();
}

/* ------------------------------------------------------------------ CABLE */

document.addEventListener('click', (e) => {
  const abre = e.target.closest('[data-aud-hoja]');
  if (abre) {
    e.preventDefault();
    abrirHoja(abre);
    return;
  }

  /* Solo cuenta si es un <button> Y está dentro de uno de los dos diálogos. Sin las dos
     comprobaciones, `closest` se traía cualquier cosa de la página que llevara la clase y
     se podía llegar a agregar una variante `undefined`. */
  const talla = e.target.closest('.aud-htalla');
  if (talla && talla.tagName === 'BUTTON' && !talla.disabled && (hoja.contains(talla) || pop.contains(talla))) {
    const dlg = hoja.contains(talla) ? hoja : pop;
    for (const o of dlg.querySelectorAll('.aud-htalla')) o.setAttribute('aria-pressed', 'false');
    talla.setAttribute('aria-pressed', 'true');
    /* Escogida a mano: el medidor deja de imponer la suya al mover la barra. */
    variante = { id: talla.dataset.id, t: talla.dataset.t };
    pintarBoton();
    return;
  }

  const paso = e.target.closest('[data-aud-paso]');
  if (paso && pop.contains(paso)) {
    mover(+paso.dataset.audPaso);
    return;
  }

  if (e.target.closest('[data-aud-medidor]')) {
    abrirPop();
    return;
  }

  const gen = e.target.closest('[data-aud-genero]');
  if (gen && pop.contains(gen)) {
    genero = gen.dataset.audGenero;
    for (const o of pop.querySelectorAll('[data-aud-genero]')) o.setAttribute('aria-pressed', String(o === gen));
    pintarPop();
    return;
  }

  const agregarBtn = e.target.closest('[data-aud-agregar]');
  if (agregarBtn) {
    agregar(agregarBtn);
    return;
  }

  const cerrar = e.target.closest('[data-aud-cerrar]');
  /* Sin restringir a los dos diálogos A PROPÓSITO: la lupa de reseñas (`aud-muro.js`) pone
     `data-aud-cerrar` en su botón y cuenta con que este cable la cierre. */
  if (cerrar) cerrar.closest('dialog')?.close();
});

pop?.querySelector('[data-aud-rango]')?.addEventListener('input', pintarMedida);

/* Tocar el fondo cierra. El `dialog` ocupa la pantalla y recibe el clic cuando cae fuera de la
   hoja. */
for (const d of [hoja, pop]) {
  d?.addEventListener('click', (e) => {
    if (e.target === d) d.close();
  });
}
